'use client';
// components/ui/DynamicIcon.tsx
import { useEffect, useState } from 'react';
import type { IconType } from 'react-icons';
import { resolveIcon } from './iconSets';

interface DynamicIconProps {
    iconName?: string;
    className?: string;
    size?: number | string;
    color?: string;
    fallback?: React.ReactNode;
    /**
     * Accessible name for the icon. Provide this only when the icon conveys
     * meaning on its own (e.g. an icon-only button). When omitted the icon is
     * treated as decorative and hidden from the accessibility tree, which is
     * correct whenever an adjacent text label or a labelled parent already
     * describes it.
     */
    title?: string;
}

export const DynamicIcon = ({
    iconName,
    className = '',
    size,
    color,
    fallback = null,
    title,
}: DynamicIconProps) => {
    // Keep the resolved icon paired with the name it was resolved for, so a
    // change to `iconName` falls back to the placeholder until the new icon
    // loads — without synchronously resetting state inside the effect.
    const [resolved, setResolved] = useState<{ name: string; Icon: IconType | null }>();

    useEffect(() => {
        if (!iconName) return;

        let active = true;
        resolveIcon(iconName).then((icon) => {
            if (active) setResolved({ name: iconName, Icon: icon });
        });

        return () => {
            active = false;
        };
    }, [iconName]);

    const IconComponent = resolved && resolved.name === iconName ? resolved.Icon : null;

    if (!iconName || !IconComponent) {
        return <>{fallback}</>;
    }

    const a11y = title
        ? { title, role: 'img' as const }
        : { 'aria-hidden': true, focusable: false };

    return <IconComponent className={className} size={size} color={color} {...a11y} />;
};

export default DynamicIcon;
